// src/hooks/useRecentPages.js
//
// Tracks the most recently visited routes. Used by the command palette
// to surface a "Recent" section when no query is typed so power users
// can flip back through the last handful of pages in two taps.
//
// Stored in localStorage (`caro.portal.recentPages`) so it survives a
// hard reload. Keeps at most 8 entries, de-duplicated by path.

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const KEY = 'caro.portal.recentPages';
const MAX = 8;

const readRecent = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeRecent = (list) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    /* ignore */
  }
};

// Paths we never store — e.g. auth callbacks and the permission-denied
// fallback would be noise in the recents surface.
const BLOCKLIST = ['/login', '/auth-callback', '/auth-error', '/permission-denied', '/health', '/'];

export function useRecentPagesTracker(labelFor = (p) => p) {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    if (!path || BLOCKLIST.includes(path)) return;
    // Treat dynamic detail routes (e.g. /alarms/:id) as their own entry
    // — we keep the raw path so "Alarm 4721" reads differently from the list.
    const label = labelFor(path) || path;
    const now = Date.now();
    const prev = readRecent().filter((x) => x.path !== path);
    const next = [{ path, label, visitedAt: now }, ...prev];
    writeRecent(next);
  }, [location.pathname, labelFor]);
}

export function useRecentPages() {
  const [recent, setRecent] = useState(() => readRecent());

  useEffect(() => {
    const refresh = () => setRecent(readRecent());
    const onStorage = (e) => { if (e.key === KEY) refresh(); };
    window.addEventListener('storage', onStorage);
    // Also refresh when this tab navigates — callers render immediately
    // but a subsequent open of the palette should see fresh data.
    const interval = setInterval(refresh, 1500);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  return recent;
}

export function clearRecentPages() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
